import { useEffect, useState } from 'react';
import { getPosts, getMediaUrl } from '../../../lib/api';
import { stripHtml } from '../../../lib/utils';
import { useNavigate } from 'react-router-dom';

interface Post {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  published_at: string;
  created_at: string;
  featured_image?: string;
}

export default function News() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await getPosts({ status: 'published' }) as Post[];
        setPosts((data ?? []).slice(0, 3));
      } catch (error) {
        console.error('投稿の取得エラー:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handlePostClick = (postId: string) => {
    navigate(`/news/${postId}`);
  };

  const handleViewAll = () => {
    navigate('/news');
  };

  return (
    <section id="news" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            お知らせ
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            最新のニュースやお知らせをご覧いただけます
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-article-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">まだお知らせがありません</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {posts.map((post) => (
                <article
                  key={post.id}
                  onClick={() => handlePostClick(post.id)}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                >
                  {post.featured_image && (
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={getMediaUrl(post.featured_image)}
                        alt=""
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      {post.category && (
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                          {post.category}
                        </span>
                      )}
                      <time className="text-sm text-gray-500" dateTime={post.published_at || post.created_at}>
                        {formatDate(post.published_at || post.created_at)}
                      </time>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 line-clamp-3 mb-4">
                      {stripHtml(post.excerpt || '')}
                    </p>
                    <div className="flex items-center text-teal-600 font-medium group-hover:gap-2 transition-all">
                      <span className="text-sm">続きを読む</span>
                      <i className="ri-arrow-right-line ml-1"></i>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center">
              <button
                onClick={handleViewAll}
                className="inline-flex items-center gap-2 px-8 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                すべてのお知らせを見る
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
